"use client";

/**
 * Cart drawer — full restyling onto @wmart/ui's <Drawer> + <CartLineItem variant="compact">.
 *
 * Currently uninstantiated in V1.0 commerce path: the StorefrontHeader's cart icon links
 * directly to `/cart` (full-page cart) rather than triggering this drawer.
 *
 * The component is preserved for future use via two anticipated triggers:
 * 1. T15 Amendment 6 — extend StorefrontHeaderProps with a `cartTrigger` slot that callers
 *    can supply to override the built-in `<a href={cartHref}>` with a drawer-toggle button.
 * 2. A toast-action wiring — "View cart" toast on add-to-cart success could open this drawer
 *    instead of navigating away from the catalog flow.
 *
 * To wire the drawer today, render it conditionally from the layout client shell and
 * control `open` from a context shared with the trigger.
 */

import type { Route } from "next";
import { useLocale, useTranslations } from "next-intl";
import { Button, CartLineItem, Drawer, Hairline, Numeric } from "@wmart/ui";
import { useCart } from "./CartProvider";
import { formatGBP } from "@/lib/format";
import { LocationShipStrip } from "@/components/LocationShipStrip";

export function CartDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const t = useTranslations("Cart");
  const locale = useLocale();
  const { cart, updateQuantity, removeItem, lastError } = useCart();
  const lines = cart?.lines ?? [];

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      side="right"
      title={t("drawerTitle")}
      width={420}
      footer={
        lines.length > 0 ? (
          <div className="flex flex-col gap-3">
            {/* Ex-VAT subtotal + VAT + gross total. B2B buyers order ex-VAT,
                but the gross "Total inc VAT" is the cash they'll actually pay,
                so it's the emphasised line. */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between text-body-sm text-ink-500">
                <span>{t("subtotalExVat")}</span>
                <Numeric>
                  {cart ? formatGBP(cart.totals.subtotalMinor, locale) : "—"}
                </Numeric>
              </div>
              <div className="flex items-baseline justify-between text-body-sm text-ink-500">
                <span>{t("vat")}</span>
                <Numeric>
                  {cart ? formatGBP(cart.totals.vatMinor, locale) : "—"}
                </Numeric>
              </div>
              <div className="flex items-baseline justify-between border-t border-ink-200 pt-1.5">
                <p className="text-body text-ink-900">{t("totalIncVat")}</p>
                <Numeric serif className="text-title-md text-ink-900">
                  {cart ? formatGBP(cart.totals.totalMinor, locale) : "—"}
                </Numeric>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href={"/cart" as Route}
                onClick={onClose}
                className="flex-1 rounded-sm outline-none focus-visible:shadow-focus"
              >
                <Button
                  full
                  variant="secondary"
                  size="md"
                  tabIndex={-1}
                >
                  {t("viewCart")}
                </Button>
              </a>
              <a
                href={"/checkout" as Route}
                onClick={onClose}
                className="flex-1 rounded-sm outline-none focus-visible:shadow-focus"
              >
                <Button full size="md" tabIndex={-1}>
                  {t("checkout")}
                </Button>
              </a>
            </div>
            {lastError ? (
              <p className="text-body-sm text-danger" role="alert">
                {lastError}
              </p>
            ) : null}
          </div>
        ) : null
      }
    >
      <LocationShipStrip />

      {lines.length === 0 ? (
        <p className="text-body text-ink-500">{t("empty")}</p>
      ) : (
        <>
          <Hairline />
          <div>
            {lines.map((line) => (
              <CartLineItem
                key={line.id}
                variant="compact"
                name={`${line.productTitle} — ${line.variantTitle}`}
                sku={line.sku}
                {...(line.imageUrl ? { photoSrc: line.imageUrl } : {})}
                unitAmountMinor={line.unitPriceMinor}
                qty={line.quantity}
                onQtyChange={(next) => {
                  if (next <= 0) {
                    void removeItem(line.id);
                  } else if (next !== line.quantity) {
                    void updateQuantity(line.id, next);
                  }
                }}
                onRemove={() => void removeItem(line.id)}
              />
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}
